/**
 * Which project the app should be working on, given what the server actually
 * returned and what the user last chose.
 *
 * Pure, and separate from `useSelectedProject`, because the decision is the part
 * worth testing: the hook only stores a string, while this is where a stale
 * choice becomes a wrong one. A persisted id outlives the thing it points at —
 * the project can be deleted, the user's access revoked, or the id can belong to
 * an organisation they were removed from — and a selection that resolves to
 * nothing binds the chat to no project without ever saying so.
 *
 * The order is deliberate:
 *   1. **Keep the stored choice if the list still has it.** Switching org and
 *      back, or a reload, must not move the user off their project.
 *   2. Otherwise the first **active** project. `status: false` projects are
 *      inactive, and defaulting into one is a worse guess than skipping it.
 *   3. Otherwise the first project at all — inactive is still better than none.
 *   4. Otherwise nothing, and the caller says so rather than looking bound.
 */
import type { Project } from "./projects";

export interface Resolution {
  /** The project to work on, or null when the organisation has none. */
  projectId: string | null;
  /**
   * True when the stored id named something the list does not contain, so the
   * caller can clear it instead of leaving it to rot in `localStorage`.
   */
  storedWasStale: boolean;
}

export function resolveSelection(
  projects: readonly Project[],
  storedId: string | null,
): Resolution {
  if (projects.length === 0) {
    // Nothing to point at. A stored id is not "stale" here — the list may simply
    // not have loaded, or the org may be empty, and discarding the user's choice
    // on an empty list would lose it every time a fetch fails.
    return { projectId: null, storedWasStale: false };
  }

  if (storedId && projects.some((p) => p.id === storedId)) {
    return { projectId: storedId, storedWasStale: false };
  }

  const fallback = projects.find((p) => p.status !== false) ?? projects[0];
  return {
    projectId: fallback?.id ?? null,
    // Only stale if something was actually stored and the list disagreed.
    storedWasStale: Boolean(storedId),
  };
}
